import type { ReactElement } from "react";
import { cx } from "@/utils/cx";

/**
 * LoadingIndicator — standalone loading spinner s role="status" + sr-only label.
 *
 * Doplňuje aria-busy state na tabulkách, lazy route placeholderech a všude tam,
 * kde Button[isLoading] nestačí (inline button loading řeší Button sám).
 *
 * Color-not-only není relevantní (rotace + sr-only text nesou význam, ne barva).
 */

export interface LoadingIndicatorProps {
    label?: string;
    size?: "sm" | "md" | "lg";
    className?: string;
}

const sizeClasses: Record<NonNullable<LoadingIndicatorProps["size"]>, string> = {
    sm: "size-4",
    md: "size-6",
    lg: "size-8",
};

export function LoadingIndicator({
    label = "Načítám…",
    size = "md",
    className,
}: LoadingIndicatorProps): ReactElement {
    return (
        <span
            data-size={size}
            role="status"
            aria-live="polite"
            className={cx("inline-flex items-center justify-center text-fg-quaternary", className)}
        >
            <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                fill="none"
                className={cx("animate-spin", sizeClasses[size])}
            >
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
                <path
                    d="M22 12a10 10 0 0 1-10 10"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                />
            </svg>
            <span className="sr-only">{label}</span>
        </span>
    );
}
